import { useDraggable } from "@dnd-kit/core"
import { Box, Tooltip } from "@mui/material"
import {
  FC,
  forwardRef,
  MutableRefObject,
  useEffect,
  KeyboardEvent,
  useCallback,
  useMemo
} from "react"
import {
  DND_ELEMENT_ATTRIBUTES,
  REMOVE_ITEM_ATTRIBUTES,
  TEST_ATTRIBUTE_ID,
  TEST_ELEMENT_IDS
} from "../../../constants"
import { faTimesCircle } from "@fortawesome/pro-solid-svg-icons"
import { generateAttrWithValue } from "../../../helpers"
import { REVIEW_BADGE_ORIGIN } from "../../badges"
import {
  BasicDragContainerWrapper,
  IndexWrapper,
  StyledReviewBadge,
  StyledBasicDragContainer,
  ContentWrapper,
  RemoveWrapper,
  RemoveButton,
  TimesCircleIcon,
  VariantIconContainer,
  VariantIcon
} from "./styles"
// import {
//   TooltipFeedbackGroup,
//   TooltipFeedbackGroupProps,
// } from '@app/components/feedback/TooltipFeedbackGroup'
import { TooltipFeedback } from "../../../feedback/TooltipFeedback"
import { faCheck } from "@fortawesome/pro-solid-svg-icons/faCheck"
import { faXmark } from "@fortawesome/pro-solid-svg-icons/faXmark"
import { NeutralSetting, Variant } from "../../../types"

export interface BasicDragContainerComponentProps {
  disabled?: boolean
  drag?: boolean
  assigned?: boolean
  placehold?: boolean
  selected?: boolean
  correct?: boolean
  correctAnswer?: React.ReactNode
  groupMode?: boolean
  // feedbackItems?: TooltipFeedbackGroupProps['items']
  fluid?: boolean
  index?: number | null
  style?: any
  onClick?: () => void
  keyDownSubscriber?: (e: KeyboardEvent<HTMLDivElement>) => void
  handleRemove?: () => void
  children: JSX.Element | JSX.Element[]
  node?: MutableRefObject<HTMLElement>
  id?: string
  variantIndex?: number
  roleAttr?: string
  accessibility?: { [key: string]: string }
  className?: string
  isCorrect?: boolean
  completed?: boolean
  isNeutralSetting?: NeutralSetting
}

export const BasicDragContainerComponent = forwardRef<
  HTMLSpanElement,
  BasicDragContainerComponentProps
>(
  (
    {
      index,
      assigned,
      disabled,
      selected,
      drag,
      correct,
      correctAnswer,
      groupMode = false,
      // feedbackItems,
      fluid = false,
      handleRemove,
      keyDownSubscriber,
      id,
      variantIndex,
      node,
      roleAttr,
      accessibility,
      className,
      completed,
      isCorrect,
      isNeutralSetting,
      ...props
    },
    ref
  ) => {
    const testAttributes =
      variantIndex || variantIndex === 0
        ? generateAttrWithValue(
            TEST_ATTRIBUTE_ID,
            TEST_ELEMENT_IDS.dragAndDropElement,
            variantIndex
          )
        : DND_ELEMENT_ATTRIBUTES

    useEffect(() => {
      if (selected && !disabled && !drag && node && node.current) {
        const containerBase = node.current.lastChild as HTMLElement
        containerBase && containerBase?.focus()
      }
    }, [id, node, selected, disabled, drag])

    const keyboardRemove = useCallback(
      (event: KeyboardEvent<HTMLButtonElement>) => {
        if (event.key === "Tab") return

        event.preventDefault()
        event.stopPropagation()

        if (event.key === " " || event.key === "Enter") {
          if (completed) return
          handleRemove()
        }
      },
      [handleRemove, completed]
    )

    const onClickRemove = useCallback(() => {
      if (completed) return

      handleRemove()
    }, [handleRemove, completed])

    const badgeClass = useMemo(() => {
      if (drag) {
        return null
      }
      if (assigned && isNeutralSetting) {
        return { variant: Variant.neutral }
      }
      if (assigned && isCorrect !== null) {
        return isCorrect
          ? { variant: Variant.correct }
          : { variant: Variant.invalid }
      }
      return null
    }, [assigned, isCorrect, drag, isNeutralSetting])

    const selectedA11yId: string = useMemo(
      () => (id ? `${id}--selected-item` : null),
      [id]
    )

    const mergedAriaDescribedBy: string = useMemo(() => {
      const fromProps = props?.["aria-describedby"] ?? null

      if (!selected || !selectedA11yId) return fromProps

      return fromProps ? `${fromProps} ${selectedA11yId}` : selectedA11yId
    }, [props, selected, selectedA11yId])

    const removeBtn = (
      <RemoveButton
        role="navigation"
        aria-label="Remove button"
        selected={selected && assigned}
        onClick={onClickRemove}
        onKeyDown={keyboardRemove}
        disabled={!assigned || completed || !!disabled}
        {...REMOVE_ITEM_ATTRIBUTES}
      >
        <TimesCircleIcon icon={faTimesCircle} />
      </RemoveButton>
    )

    const showVariantIcon = (isNeutralSetting: NeutralSetting, badgeClass) => {
      if (badgeClass.variant === "correct" && !isNeutralSetting) {
        return <VariantIcon icon={faCheck} />
      } else if (badgeClass.variant === "invalid" && !isNeutralSetting) {
        return <VariantIcon icon={faXmark} />
      }
      return undefined
    }

    return (
      <BasicDragContainerWrapper
        onKeyDown={keyDownSubscriber}
        ref={ref}
        className={className}
        {...accessibility}
        {...(roleAttr ? { role: roleAttr } : {})}
      >
        {!!index && <IndexWrapper>{index}.</IndexWrapper>}
        <StyledReviewBadge
          // as={groupMode ? TooltipFeedbackGroup : TooltipFeedback}
          as={groupMode ? Box : TooltipFeedback}
          correct={correct}
          correctAnswer={correctAnswer}
          // items={feedbackItems}
          anchorOrigin={REVIEW_BADGE_ORIGIN.topLeft}
        >
          <StyledBasicDragContainer
            data-identifier={id}
            component="span"
            assigned={assigned}
            disabled={disabled}
            selected={selected}
            drag={drag}
            fluid={fluid}
            aria-describedby={mergedAriaDescribedBy}
            {...props}
            {...testAttributes}
            tabIndex={disabled ? -1 : 0}
            {...badgeClass}
          >
            <ContentWrapper component="span">{props.children}</ContentWrapper>
            {selected && selectedA11yId ? (
              <span id={selectedA11yId} className="sr-only">
                selected
              </span>
            ) : null}
            <RemoveWrapper aria-hidden role="tooltip">
              {assigned ? (
                <Tooltip
                  title="Remove"
                  placement="top"
                  componentsProps={{
                    tooltip: {
                      sx: {
                        marginBottom: "0 !important"
                      }
                    }
                  }}
                >
                  {removeBtn}
                </Tooltip>
              ) : (
                removeBtn
              )}
            </RemoveWrapper>
            {badgeClass && !isNeutralSetting ? (
              <VariantIconContainer variant={badgeClass.variant}>
                {showVariantIcon(isNeutralSetting, badgeClass)}
              </VariantIconContainer>
            ) : undefined}
          </StyledBasicDragContainer>
        </StyledReviewBadge>
      </BasicDragContainerWrapper>
    )
  }
)

interface DragContainerComponentProps extends BasicDragContainerComponentProps {
  id: string
  resetDragState?: () => void
  containerId?: string
  isNeutralSetting?: NeutralSetting
}

export const DragContainerComponent: FC<DragContainerComponentProps> = ({
  id,
  containerId,
  disabled,
  placehold,
  isNeutralSetting,
  ...props
}) => {
  const { setNodeRef, attributes, listeners, node } = useDraggable({
    id,
    disabled,
    data: { containerId }
  })

  return (
    <BasicDragContainerComponent
      id={id}
      disabled={disabled}
      ref={setNodeRef}
      node={node}
      accessibility={{
        ...(placehold ? { "aria-label": " " } : {})
      }}
      isNeutralSetting={isNeutralSetting}
      {...listeners}
      {...attributes}
      {...props}
    />
  )
}
